import React, { useEffect, useRef, useState } from 'react';
import {
  parsePersonalizeFile,
  PersonalizeParseResult,
} from '../../utils/excelPersonalize';
import { ZIMBABWE_PHONE_HINT } from '../../utils/phoneNumber';

interface PersonalizeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (parsed: PersonalizeParseResult) => void;
}

export const PersonalizeModal: React.FC<PersonalizeModalProps> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  const [parsed, setParsed] = useState<PersonalizeParseResult | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isReading, setIsReading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) {
      setParsed(null);
      setIsDragging(false);
      setIsReading(false);
      setError(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const processFile = async (file: File) => {
    setError(null);
    setParsed(null);
    setIsReading(true);
    try {
      const result = await parsePersonalizeFile(file);
      if (result.rows.length === 0) {
        setError(
          `No valid mobile numbers were found in the first column. ${ZIMBABWE_PHONE_HINT}`
        );
        return;
      }
      setParsed(result);
      if (result.skipped > 0) {
        setError(
          `${result.rows.length} valid row(s) loaded. ${result.skipped} row(s) had an invalid mobile number. ${ZIMBABWE_PHONE_HINT}`
        );
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to read the file.');
    } finally {
      setIsReading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      processFile(file);
    }
    event.target.value = '';
  };

  const handleSave = () => {
    if (!parsed) return;
    onSave(parsed);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Personalize</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center ${
            isDragging ? 'border-[#FF5722] bg-orange-50' : 'border-gray-300'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".csv,.txt,.xlsx,.xls"
            className="hidden"
          />
          <div className="space-y-4">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <div className="text-sm text-gray-600">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-[#FF5722] hover:text-[#F4511E] font-medium"
              >
                {isReading ? 'Reading file...' : 'Click to upload'}
              </button>
              {' or drag and drop'}
            </div>
            <p className="text-xs text-gray-500">
              CSV or Excel file. First row must be headings. First column must be the mobile number (263XXXXXXXXX).
            </p>
          </div>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 text-red-600 rounded-md text-sm">
            {error}
          </div>
        )}

        {parsed && (
          <div className="mt-6">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-medium text-gray-700">
                Preview ({parsed.previewRows.length} of {parsed.rows.length} valid row(s))
              </h3>
              <span className="text-xs text-gray-500 truncate ml-2">
                {parsed.fileName}
              </span>
            </div>
            <div className="border rounded-md overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    {parsed.columns.map((column) => (
                      <th
                        key={column}
                        className="px-3 py-2 text-left font-medium text-gray-700 whitespace-nowrap"
                      >
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {parsed.previewRows.map((row, index) => (
                    <tr key={index} className="border-t border-gray-100">
                      {parsed.columns.map((column, colIndex) => (
                        <td
                          key={`${index}-${column}`}
                          className={`px-3 py-2 text-gray-600 whitespace-nowrap ${
                            colIndex === 0 && !row.isValid ? 'text-red-600' : ''
                          }`}
                        >
                          {row.values[colIndex] || ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="mt-6 flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!parsed}
            className={`px-4 py-2 rounded-md transition-colors ${
              parsed
                ? 'bg-[#FF5722] text-white hover:bg-[#F4511E]'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            Personalize
          </button>
        </div>
      </div>
    </div>
  );
};
